import { resolveSegmentKey } from "@/helpers/templates";
import { appLocales, getLocalFromPrismic, Locales } from "@/lib/i18n";
import { resolveTranslatedPath } from "@/lib/i18n/functions";
import { createClient } from "@/prismicio";
import { CategorieDocument } from "@/prismicio-types";
import { AlternateLanguage } from "@prismicio/client";
import { Metadata } from "next";

const getMetadataBase = () => {
  const frontUrl = process.env.NEXT_PUBLIC_FRONT_URL || "http://localhost:3000";

  try {
    return new URL(frontUrl);
  } catch {
    return new URL("http://localhost:3000");
  }
};

export const generateSEO = async (
  document: any,
  locale: Locales,
  isFrontPage = false,
  isCategory = false
): Promise<Metadata> => {
  if (!document || !document?.data) return {};

  const defaultTitle = document.data?.meta_title ? document.data.meta_title + "" : document.data?.title + "";

  // Add the locale document to the alternate_languages
  const allDocuments = [...document?.alternate_languages, document];

  const alternatesArray = await Promise.all(
    (allDocuments || []).map(async (alternate: AlternateLanguage) => {
      const langKey = getLocalFromPrismic(alternate.lang);
      const url = isCategory ? await resolveAlternates(alternate) : resolveTranslatedPath(alternate);
      return [langKey, url] as [string, string];
    })
  );

  const alternates: Record<string, string> = Object.fromEntries(alternatesArray);

  return {
    metadataBase: getMetadataBase(),
    title: isFrontPage
      ? {
          absolute: defaultTitle,
        }
      : defaultTitle,
    description: document.data.meta_description,
    openGraph: {
      title: document.data.og_title || defaultTitle,
      description: document.data.og_description || document.data.meta_description,
      images: document.data.og_image?.url ? [document.data.og_image.url] : undefined,
    },
    twitter: {
      title: document.data.twitter_title || document.data.og_title || defaultTitle,
      description: document.data.twitter_description || document.data.og_description || document.data.meta_description,
      images: document.data.twitter_image?.url
        ? [document.data.twitter_image.url]
        : document.data.og_image?.url
          ? [document.data.og_image.url]
          : undefined,
    },
    alternates: {
      canonical: document.data?.url_canonical || alternates[locale] || undefined,

      languages: alternates,
    },
    robots: {
      index: document.data?.index || true,
      follow: document.data?.follow || true,
    },
    // Favicon and metadata
    icons: {
      icon: [
        {
          url: "/favicon/favicon-96x96.png",
          sizes: "96x96",
          type: "image/png",
        },
        {
          url: "/favicon/favicon.svg",
          type: "image/svg+xml",
        },
      ],
      shortcut: "/favicon/favicon.ico",
      apple: {
        url: "/favicon/apple-touch-icon.png",
        sizes: "180x180",
      },
    },
    manifest: "/favicon/manifest.json",
  };
};

export const resolveAlternates = async (document: AlternateLanguage, resolveUri = true): Promise<any> => {
  const client = createClient();

  try {
    const categories = await client.getAllByType("categorie", {
      fetchLinks: ["categorie.name"],
      lang: document.lang,
    });

    if (!categories) {
      return "/";
    }

    const currentCategory = categories.find((category) => category.id === document.id);

    if (!currentCategory) {
      return "/";
    }

    let categoryTree = [currentCategory];

    if (!currentCategory) {
      return;
    }

    if ("id" in currentCategory?.data?.parentCategorie) {
      categoryTree = findCategoryParent(currentCategory.data.parentCategorie.id, categories, categoryTree);
    }

    if (resolveUri) {
      const lang = getLocalFromPrismic(document.lang);
      const segment = resolveSegmentKey(lang, "categories");

      const categorieSlug = categoryTree.reduce((acc, category) => {
        if (!category || !category.uid) {
          return acc;
        }

        const categorySlug = category.uid;
        return `${acc}/${categorySlug}`;
      }, "");

      return [lang !== appLocales.defaultLocale ? `/${lang}/${segment}` : "/" + segment, categorieSlug].join("");
    }

    return categoryTree;
  } catch (error) {
    console.error("Error fetching categories:", error);
    return "/";
  }
};

const findCategoryParent = (
  categoryParentId: string,
  categories: CategorieDocument[],
  categoryTree: CategorieDocument[]
): CategorieDocument[] => {
  // Find the parent category
  const parentCategory = categories.find((category) => category.id === categoryParentId);

  if (parentCategory) {
    categoryTree.unshift(parentCategory);
    // If the parent category has a parent, continue searching
    if ("id" in parentCategory.data.parentCategorie) {
      return findCategoryParent(parentCategory.data.parentCategorie.id, categories, categoryTree);
    }
  }

  // If no parent category is found or no more parents, return the tree
  return categoryTree;
};
